<main class="checkout">
  <div class="checkout__container">
    <header class="checkout__header">
      <h1 class="checkout__title">Completar pedido</h1>
      @if ((cart()?.productsQuantity ?? 0) > 0) {
        <p class="checkout__subtitle">
          {{ cart()?.productsQuantity }} productos
        </p>
      }
    </header>

    <!-- Aviso de stock de singles compartido con la tienda física -->
    @if (hasSingles()) {
      <p class="checkout__singles-notice" role="status">
        Las cartas individuales (singles) también se venden en nuestra tienda física.
        Si al confirmar tu pedido no hubiera stock suficiente de alguna, te contactaremos
        para ofrecerte una solución o una alternativa.
      </p>
    }

    <!-- Resumen colapsable (sólo visible en mobile vía CSS) -->
    <details class="checkout__summary-mobile">
      <summary class="checkout__summary-mobile-summary">
        <span>Ver resumen del pedido</span>
      </summary>
      <div class="checkout__summary-mobile-body">
        <app-cart-summary
          [deliveryMethod]="deliveryMethodValue()"
          [deliveryPrice]="computedDeliveryPrice()"
        />
      </div>
    </details>

    <div class="checkout__grid">
      <form class="checkout__form" [formGroup]="checkoutForm" (ngSubmit)="onSubmit()">
        <!-- ── Contacto ── -->
        <section class="checkout__section" aria-labelledby="checkout-contact-title" [formGroup]="contactGroup">
          <header class="checkout__section-header">
            <h2 id="checkout-contact-title" class="checkout__section-title">
              Contacto
            </h2>
            @if (!isAuthenticated()) {
              <p class="checkout__login-banner">
                @if (authoritativePoints() > 0) {
                  <app-pp-coin [size]="16" />{{ withPoints(ppCopy.guestBanner, authoritativePoints()) }}
                } @else {
                  ¿Ya tienes cuenta?
                }
                <button type="button" class="checkout__login-link" (click)="onOpenLogin()">
                  @if (authoritativePoints() > 0) {
                    {{ ppCopy.guestBannerCta }}
                  } @else {
                    Inicia sesión
                  }
                </button>
              </p>
            } @else {
              <p class="checkout__login-banner checkout__login-banner--logged">
                Sesión iniciada como
                <strong>{{ currentUser()?.email }}</strong>
              </p>
              @if (pointsProjection(); as proj) {
                <p class="checkout__pp-banner">
                  <app-pp-coin [size]="16" />{{ withBalance(ppCopy.projection, proj.a, proj.b) }}
                </p>
                @if (reachesTierWithPurchase()) {
                  <p class="checkout__pp-banner checkout__pp-banner--tier">{{ ppCopy.tierReached }}</p>
                } @else if (pointsMissingAfterPurchase(); as missing) {
                  <p class="checkout__pp-banner">
                    <app-pp-coin [size]="16" />{{ withMissing(ppCopy.tierMissing, missing) }}
                  </p>
                }
              }
            }
          </header>

          <div class="checkout__field-grid">
            <app-input id="checkout-contact-name" label="Nombre" type="text" formControlName="name" [error]="errorFor('contact.name', 'El nombre')" />
            <app-input id="checkout-contact-surnames" label="Apellidos" type="text" formControlName="surnames" [error]="errorFor('contact.surnames', 'Los apellidos')" />
            <app-input id="checkout-contact-email" label="Email" type="email" formControlName="email" [error]="errorFor('contact.email', 'El email')" />
            <app-input id="checkout-contact-phone" label="Teléfono (opcional)" type="tel" placeholder="8888 8888" formControlName="phone" />
          </div>
        </section>

        <!-- ── Envío ── -->
        <section class="checkout__section" aria-labelledby="checkout-delivery-title" [formGroup]="deliveryGroup">
          <h2 id="checkout-delivery-title" class="checkout__section-title">
            Envío
          </h2>

          <!-- Selector de método (5 opciones) -->
          <div class="checkout__delivery-options" role="radiogroup" aria-labelledby="checkout-delivery-title">
            <label class="checkout__delivery-option">
              <input
                type="radio"
                name="method"
                value="ps"
                formControlName="method"
                aria-describedby="checkout-delivery-meta-ps"
              />
              <span class="checkout__delivery-option-body">
                <span class="checkout__delivery-option-title">
                  Retirar mi pedido en <strong>Perrón Store</strong>
                </span>
                <span id="checkout-delivery-meta-ps" class="checkout__delivery-option-meta">
                  Gratis
                </span>
              </span>
            </label>

            <label class="checkout__delivery-option">
              <input
                type="radio"
                name="method"
                value="uber"
                formControlName="method"
                aria-describedby="checkout-delivery-meta-uber"
              />
              <span class="checkout__delivery-option-body">
                <span class="checkout__delivery-option-title">
                  Enviaré un mensajero <strong>(Uber Flash, etc.)</strong>
                </span>
                <span id="checkout-delivery-meta-uber" class="checkout__delivery-option-meta">
                  Mensajero por tu cuenta
                </span>
              </span>
            </label>

            <label class="checkout__delivery-option" [class.checkout__delivery-option--disabled]="intershopBlocked()">
              <input
                type="radio"
                name="method"
                value="other_shop"
                formControlName="method"
                [attr.disabled]="intershopBlocked() ? '' : null"
                aria-describedby="checkout-delivery-meta-other-shop"
              />
              <span class="checkout__delivery-option-body">
                <span class="checkout__delivery-option-title">
                  Enviar pedido a <strong>Otra Tienda</strong>
                </span>
                <span id="checkout-delivery-meta-other-shop" class="checkout__delivery-option-meta">
                  @if (intershopBlocked()) {
                    No disponible: tu carrito incluye productos que no son singles
                  } @else {
                    Solo para singles
                  }
                </span>
              </span>
            </label>

            <label class="checkout__delivery-option">
              <input
                type="radio"
                name="method"
                value="home"
                formControlName="method"
                aria-describedby="checkout-delivery-meta-home"
              />
              <span class="checkout__delivery-option-body">
                <span class="checkout__delivery-option-title">
                  Recibir mi pedido en <strong>Mi Dirección</strong>
                </span>
                <span id="checkout-delivery-meta-home" class="checkout__delivery-option-meta">
                  Correos de Costa Rica
                  <span class="checkout__delivery-option-meta-detail">
                    Se preparan y entregan a Correos los martes, miércoles y jueves.
                  </span>
                </span>
              </span>
            </label>

            @if (psDeliveryEnabled) {
              <label class="checkout__delivery-option" [class.checkout__delivery-option--disabled]="psDeliveryOptionDisabled()">
                <input
                  type="radio"
                  name="method"
                  value="ps_delivery"
                  formControlName="method"
                  [attr.disabled]="psDeliveryOptionDisabled() ? '' : null"
                  aria-describedby="checkout-delivery-meta-ps-delivery"
                />
                <span class="checkout__delivery-option-body">
                  <span class="checkout__delivery-option-title">
                    {{ psDeliveryCopy.label }}
                  </span>
                  <span id="checkout-delivery-meta-ps-delivery" class="checkout__delivery-option-meta">
                    @if (psDeliveryOptionDisabled()) {
                      {{ psDeliveryCopy.disabledNote }}
                    } @else {
                      @if (psDeliveryPriceExact()) {
                        {{ psDeliveryPrice() | crc }}
                      } @else {
                        Desde {{ psDeliveryPrice() | crc }}
                      }
                      · {{ psDeliveryCopy.coverageNote }}
                    }
                  </span>
                </span>
              </label>
            }
          </div>

          <!-- Fuera del radiogroup: es contenido complementario, no una opción de radio (a11y). -->
          @if (psDeliveryEnabled) {
            <button
              type="button"
              class="checkout__coverage-link"
              (click)="destinationsOpen.set(true)"
            >
              {{ psDeliveryCopy.destinationsLink }}
            </button>
          }

          <!-- Banner contextual -->
          @if (deliveryMethodHint(); as hint) {
            <p class="checkout__delivery-hint" role="status">{{ hint }}</p>
          }

          <!-- Selector de tienda partner (solo other_shop) -->
          @if (requiresPartnerShop()) {
            <div class="checkout__field-block">
              <label for="checkout-shop-name" class="checkout__field-label">
                Selecciona la tienda
              </label>
              <div class="checkout__select-wrap">
                <select id="checkout-shop-name" class="checkout__select" formControlName="shop_name">
                  <option value="" disabled>— Selecciona una tienda —</option>
                  @for (shop of partnerShops(); track shop.id) {
                    <option [value]="shop.name">
                      {{ shop.name }} — {{ +shop.price | crc }}
                    </option>
                  }
                </select>
                <span class="checkout__select-chevron" aria-hidden="true">▾</span>
              </div>
              @if (errorFor('delivery.shop_name', 'La tienda partner'); as err) {
                <span class="checkout__field-error" role="alert">{{ err }}</span>
              }
            </div>
          }

          <!-- Bloque de dirección (home / ps_delivery) -->
          @if (requiresAddress()) {
            <div
              class="checkout__address"
              [formGroup]="addressGroup"
              [attr.aria-busy]="!provinces() && !locationsLoadError() ? 'true' : null"
            >
              @if (locationsLoadError()) {
                <p class="checkout__address-fallback-warning" role="alert">
                  No pudimos cargar el catálogo de ubicaciones. Por favor,
                  escribe provincia, cantón y distrito manualmente más abajo.
                </p>
              }

              <div class="checkout__field-grid">
                <div class="checkout__field-block">
                  <label for="checkout-province" class="checkout__field-label">
                    Provincia
                  </label>
                  <div class="checkout__select-wrap">
                    <select id="checkout-province" class="checkout__select" formControlName="province">
                      <option value="" disabled>— Selecciona —</option>
                      @for (p of provinces(); track p.name) {
                        <option [value]="p.name">{{ p.name }}</option>
                      }
                    </select>
                    <span class="checkout__select-chevron" aria-hidden="true">▾</span>
                  </div>
                  @if (errorFor('delivery.address.province', 'La provincia'); as err) {
                    <span class="checkout__field-error" role="alert">{{ err }}</span>
                  }
                </div>

                <div class="checkout__field-block">
                  <label for="checkout-canton" class="checkout__field-label">
                    Cantón
                  </label>
                  <div class="checkout__select-wrap">
                    <select id="checkout-canton" class="checkout__select" formControlName="canton" [attr.disabled]="!provinceValue() || availableCantons().length === 0 ? '' : null">
                      <option value="" disabled>— Selecciona —</option>
                      @for (c of availableCantons(); track c.name) {
                        <option [value]="c.name">{{ c.name }}</option>
                      }
                    </select>
                    <span class="checkout__select-chevron" aria-hidden="true">▾</span>
                  </div>
                  @if (errorFor('delivery.address.canton', 'El cantón'); as err) {
                    <span class="checkout__field-error" role="alert">{{ err }}</span>
                  }
                </div>

                <div class="checkout__field-block">
                  <label for="checkout-district" class="checkout__field-label">
                    Distrito
                  </label>
                  <div class="checkout__select-wrap">
                    <select id="checkout-district" class="checkout__select" formControlName="district" [attr.disabled]="!cantonValue() || availableDistricts().length === 0 ? '' : null">
                      <option value="" disabled>— Selecciona —</option>
                      @for (d of availableDistricts(); track d) {
                        <option [value]="d">{{ d }}</option>
                      }
                    </select>
                    <span class="checkout__select-chevron" aria-hidden="true">▾</span>
                  </div>
                  @if (errorFor('delivery.address.district', 'El distrito'); as err) {
                    <span class="checkout__field-error" role="alert">{{ err }}</span>
                  }
                </div>

                <app-input id="checkout-hood" label="Barrio o residencial (opcional)" type="text" formControlName="hood" placeholder="Ej. Residencial Los Arcos, etapa 2" />
              </div>

              <div class="checkout__field-block">
                <label for="checkout-directions" class="checkout__field-label">
                  Señas del domicilio
                </label>
                <textarea id="checkout-directions" class="checkout__textarea" formControlName="directions" rows="3" maxlength="300"
                  placeholder="Ej. de la iglesia católica 200m al sur, casa verde con portón blanco"></textarea>
                @if (errorFor('delivery.address.directions', 'Las señas'); as err) {
                  <span class="checkout__field-error" role="alert">{{ err }}</span>
                }
              </div>
            </div>
          }
        </section>

        <!-- ── Pago ── -->
        <section class="checkout__section" aria-labelledby="checkout-payment-title" [formGroup]="paymentGroup">
          <h2 id="checkout-payment-title" class="checkout__section-title">
            Pago
          </h2>

          <!-- Métodos DIRECTOS (sinpe/transfer/online). Ocultos en modalidad
               apartado: ahí el método del abono lo elige el selector del 30%. -->
          @if (!isLayaway()) {
            <div class="checkout__payment-options" role="radiogroup" aria-labelledby="checkout-payment-title">
              @for (option of paymentOptions; track option.key) {
                <label class="checkout__delivery-option">
                  <input
                    type="radio"
                    name="payment_method"
                    [value]="option.key"
                    formControlName="payment_method"
                    [attr.aria-describedby]="'checkout-payment-meta-' + option.key"
                  />
                  <span class="checkout__delivery-option-body">
                    <span class="checkout__delivery-option-title" [innerHTML]="option.title"></span>
                    <span [id]="'checkout-payment-meta-' + option.key" class="checkout__delivery-option-meta">
                      {{ option.meta }}
                    </span>
                  </span>
                </label>
              }
            </div>
          }

          <!-- Modalidad APARTADO: toggle independiente de los métodos directos. Deshabilitado
               (no oculto) bajo el umbral mínimo de mercancía (D2, checkout-apartados-umbral-plan.md). -->
          <!-- El estado disabled del control lo gestiona Reactive Forms (control.disable()/enable()
               en el constructor, D2) — no un [attr.disabled] manual junto a formControlName. -->
          <label class="checkout__layaway-toggle" [class.checkout__layaway-toggle--disabled]="!layawayEligible()">
            <input
              type="checkbox"
              formControlName="is_layaway"
              aria-describedby="checkout-layaway-toggle-meta"
            />
            <span class="checkout__layaway-toggle-body">
              <span class="checkout__layaway-toggle-title">{{ layawayCopy.title }}</span>
              <span id="checkout-layaway-toggle-meta" class="checkout__layaway-toggle-meta">
                @if (layawayEligible()) {
                  {{ layawayToggleMeta() }}
                } @else {
                  {{ layawayDisabledHint() }}
                }
              </span>
            </span>
          </label>

          <!-- Selector del método del abono inicial (30%), tras aceptar el
               consentimiento. SINPE / transferencia / TARJETA (Pasarela 5: tarjeta habilitada). -->
          @if (isLayaway() && layawayConsented()) {
            <fieldset class="checkout__layaway-selector">
              <legend class="checkout__layaway-selector-title">{{ layawaySelectorCopy.title }}</legend>
              <!-- Reutiliza `paymentOptions` (mismos título + subtítulo que los métodos directos).
                   Tarjeta solo con la pasarela ONVO activa (mismo gate que onSubmit/D2). -->
              <div class="checkout__payment-options" role="radiogroup">
                @for (option of paymentOptions; track option.key) {
                  @if (option.key !== 'online' || cardEnabled) {
                    <label class="checkout__delivery-option">
                      <input
                        type="radio"
                        name="payment_method"
                        [value]="option.key"
                        formControlName="payment_method"
                        [attr.aria-describedby]="'checkout-layaway-meta-' + option.key"
                      />
                      <span class="checkout__delivery-option-body">
                        <span class="checkout__delivery-option-title" [innerHTML]="option.title"></span>
                        <span [id]="'checkout-layaway-meta-' + option.key" class="checkout__delivery-option-meta">
                          {{ option.meta }}
                        </span>
                      </span>
                    </label>
                  }
                }
              </div>
            </fieldset>
          }

          @if (errorFor('payment.payment_method', 'El método de pago'); as err) {
            <span class="checkout__field-error" role="alert">{{ err }}</span>
          }
        </section>

        <div class="checkout__submit">
          <!-- Con líneas de Acceso VIP bloqueadas el pedido no puede salir
               (R4): el servidor lo cortaría con un 403 en `reserve` /
               `payment-intent`, así que lo decimos antes de intentarlo. -->
          @if (blockedLinesNotice(); as notice) {
            <p class="checkout__blocked-notice" role="alert">{{ notice }}</p>
          }
          <app-button
            variant="primary"
            size="lg"
            type="submit"
            [disabled]="submitting() || hasBlockedLines()"
            [loading]="submitting()"
          >
            Confirmar pedido
          </app-button>
        </div>
      </form>

      <aside class="checkout__sidebar" aria-label="Resumen del pedido">
        <app-cart-summary
          [deliveryMethod]="deliveryMethodValue()"
          [deliveryPrice]="computedDeliveryPrice()"
        />
      </aside>
    </div>
  </div>

  <!-- Consentimiento de apartados (al activar el toggle «Apartar con abonos»). -->
  <app-blocking-notice-modal
    [open]="layawayNoticeOpen()"
    [title]="layawayCopy.title"
    [body]="layawayCopy.body"
    [emphasis]="layawayCopy.emphasis"
    [mascotSrc]="layawayCopy.mascot"
    [ackLabel]="layawayCopy.ack"
    [ctaLabel]="layawayCopy.cta"
    (acknowledge)="onLayawayAcknowledge()"
  />

  <!-- Aviso del comprobante (al elegir SINPE/transferencia). La mascota va a la
       derecha, «leyendo» el aviso (reading_at_left.png, 486×640). -->
  <app-blocking-notice-modal
    [open]="proofNoticeOpen()"
    [title]="proofCopy.title"
    [body]="proofCopy.body"
    [mascotSrc]="proofCopy.mascot"
    mascotPosition="right"
    [mascotWidth]="486"
    [mascotHeight]="640"
    [ackLabel]="proofCopy.ack"
    [ctaLabel]="proofCopy.cta"
    (acknowledge)="onProofNoticeAcknowledge()"
  />

  <!-- Aviso NO bloqueante al elegir Uber Flash: solo invita a coordinar por
       WhatsApp, nunca impide continuar (D2). -->
  <app-uber-flash-notice-modal
    [open]="uberNoticeOpen()"
    [title]="uberNoticeCopy.title"
    [body]="uberNoticeCopy.body"
    [whatsappUrl]="uberNoticeCopy.whatsappUrl"
    [whatsappLabel]="uberNoticeCopy.whatsappLabel"
    [ctaLabel]="uberNoticeCopy.cta"
    (acknowledge)="onUberNoticeAcknowledge()"
  />

  <!-- Datos de pago + comprobante OBLIGATORIO (SINPE/transferencia). El preorder
       se crea al «Finalizar» con la imagen adjunta (create-on-finalize). -->
  <app-payment-data-modal
    [open]="paymentModalOpen()"
    [method]="paymentModalMethod()"
    [isLayaway]="paymentModalIsLayaway()"
    [amountColones]="paymentModalAmount()"
    [remainingColones]="paymentModalRemaining()"
    [submitting]="paymentModalSubmitting()"
    [errorMessage]="paymentModalError()"
    [countdownExpiresAt]="reservationExpiresAt()"
    [countdownLabel]="reservationTimerLabel"
    (finalize)="onPaymentFinalize($event)"
    (dismiss)="onPaymentDismiss()"
  />

  <!-- Countdown del HOLD gobernado por el PADRE (una sola fuente del evento de expiración): el modal
       solo MUESTRA la cuenta atrás; este componente oculto dispara (expired) al agotarse el hold y
       hace el teardown del modal (espejo de prepurchase-modal.onReservationExpired). -->
  @if (paymentModalOpen() && reservationExpiresAt()) {
    <app-countdown
      class="hidden"
      [expiresAt]="reservationExpiresAt()"
      (expired)="onReservationExpired()"
    />
  }

  <!-- Aviso de cobertura: el mensajero no llega al cantón elegido → cambio a Correos. -->
  <app-coverage-notice-dialog
    [open]="coverageDialogOpen()"
    [title]="psDeliveryCopy.dialog.title"
    [body]="psDeliveryCopy.dialog.body"
    [ctaLabel]="psDeliveryCopy.dialog.cta"
    (acknowledge)="onCoverageDialogAcknowledge()"
  />

  <!-- Modal informativo: provincias, tarifas y cantones del Mensajero Perrón Store. -->
  <app-ps-delivery-destinations-dialog
    [open]="destinationsOpen()"
    [title]="psDeliveryCopy.destinations.title"
    [intro]="psDeliveryCopy.destinations.intro"
    [ctaLabel]="psDeliveryCopy.destinations.cta"
    (close)="destinationsOpen.set(false)"
  />
</main>
